'use client';

import { onlineOrderingProductImage } from '@trashlab/api/src/domains/pricing/shared/product-illustration';

import {
  Box,
  Flex,
  Text,
  Heading,
  Grid,
  Container,
  Separator,
} from '@radix-ui/themes';
import StorageImage from '@trashlab/sitesapp/src/organizations/_default/components/shared/storage-image';
import {
  IconRuler3,
  IconPackages,
  IconCalendar,
  IconRefresh,
  IconTruckDelivery,
  IconArrowsExchange,
} from '@tabler/icons-react';
import { Button } from '@trashlab/sitesapp/src/organizations/_default/template/components/shared/button';
import currency from 'currency.js';
import Card from '@trashlab/sitesapp/src/organizations/_default/template/components/shared/card';
import { IconCheckmark } from '@trashlab/sitesapp/src/organizations/_default/template/components/icons/icons';
import Breadcrumbs from '@trashlab/sitesapp/src/organizations/_default/template/components/shared/breadcrumbs';
import { trpcPricingListSkusOpen } from '@trashlab/api/src/generated/hooks';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/_default/contexts/use-tenant-context';
import { useShoppingCart } from '@trashlab/sitesapp/src/organizations/_default/hooks/use-shopping-cart';
import { useNavigate } from '@tanstack/react-router';

// Best Deal Dumpsters org ID
const BEST_DEAL_ORG_ID = 'd74bb19c-004b-479d-adaa-9e846d25dac7';

// Parse cubic yardage from string (e.g., "10", "10YD", "10 Yard" -> 10)
const parseCubicYardage = (cubicYardage: string | number | null | undefined): number | null => {
  if (!cubicYardage) return null;
  const str = String(cubicYardage);
  const match = str.match(/^(\d+)/);
  return match ? parseInt(match[1], 10) : null;
};

// Best Deal specific dumpster information by cubic yardage
const getBestDealDumpsterInfo = (cubicYardage: string | number | null | undefined) => {
  const yards = parseCubicYardage(cubicYardage);
  if (!yards) return null;

  const info: Record<number, { dimensions: string; weight: string; features: string[] }> = {
    10: {
      dimensions: "12' x 8' x 4'",
      weight: 'Up to 2 tons',
      features: [
        'Small garage/basement cleanouts',
        'Small room remodel or roof replacement',
        'Equivalent to ~5 pickup truck loads',
        '7 day rental period included',
      ],
    },
    15: {
      dimensions: "12' x 8' x 5'",
      weight: 'Up to 3 tons',
      features: [
        'Medium garage/basement cleanouts',
        'Medium bathroom remodel or large roof replacement',
        'Equivalent to ~7.5 pickup truck loads',
        '7 day rental period included',
      ],
    },
    20: {
      dimensions: "17' x 8' x 6'",
      weight: 'Up to 4 tons',
      features: [
        'Large cleanouts & remodels',
        'Flooring removal for large homes',
        'Equivalent to ~10 pickup truck loads',
        '7 day rental period included',
      ],
    },
    30: {
      dimensions: "22' x 8' x 6'",
      weight: 'Up to 5 tons',
      features: [
        'Extra large cleanouts & home remodels',
        'Commercial roofing jobs',
        'Equivalent to ~15 pickup truck loads',
        '7 day rental period included',
      ],
    },
  };

  return info[yards] || null;
};

const getFrequencyLabel = (frequency?: string, interval?: number) => {
  if (!frequency) return '';

  const intervalStr = interval && interval > 1 ? `${interval} ` : '';

  switch (frequency) {
    case 'DAILY':
      return `${intervalStr}day${interval && interval > 1 ? 's' : ''}`;
    case 'WEEKLY':
      return `${intervalStr}week${interval && interval > 1 ? 's' : ''}`;
    case 'MONTHLY':
      return `${intervalStr}month${interval && interval > 1 ? 's' : ''}`;
    case 'YEARLY':
      return `${intervalStr}year${interval && interval > 1 ? 's' : ''}`;
    default:
      return frequency.toLowerCase();
  }
};

const formatBillingCycleAnchor = (billingCycleAnchor?: string) => {
  if (!billingCycleAnchor) return 'Service date';

  switch (billingCycleAnchor) {
    case 'SERVICE_DATE':
      return 'Service date (delivery date)';
    case 'FIRST_OF_MONTH':
      return 'First of month';
    case 'LAST_DAY_OF_MONTH':
      return 'Last day of month';
    case 'CUSTOM_DAY_OF_MONTH':
      return 'Custom day of month';
    case 'CUSTOM_DAY_OF_WEEK':
      return 'Custom day of week';
    case 'FIRST_OF_QUARTER':
      return 'First day of quarter (Jan 1, Apr 1, Jul 1, Oct 1)';
    default:
      return billingCycleAnchor.replace(/_/g, ' ').toLowerCase();
  }
};

const DumpsterDetailsSkeleton = () => {
  return (
    <Container size="4" p="8">
      <Box className="h-8 w-48 bg-gray-200 rounded-md animate-pulse mb-8" />
      <Grid columns={{ initial: '1', md: '2' }}>
        <Box className="relative p-8 rounded-xl flex items-center justify-center">
          <Box className="w-full h-[300px] bg-gray-200 rounded-md animate-pulse" />
        </Box>
        <Card direction="column" gap="4">
          <Box className="h-10 w-3/4 bg-gray-200 rounded-md animate-pulse" />
          <Flex align="center" gap="2">
            <Box className="h-5 w-32 bg-gray-200 rounded-md animate-pulse" />
            <Separator size="4" orientation="vertical" />
            <Box className="h-5 w-24 bg-gray-200 rounded-md animate-pulse" />
          </Flex>
          <Box className="h-16 w-full bg-gray-200 rounded-md animate-pulse" />
          <Flex direction="row" gap="4">
            {[1, 2, 3].map((i) => (
              <Box
                key={i}
                className="h-8 w-32 bg-gray-200 rounded-md animate-pulse"
              />
            ))}
          </Flex>
          <Separator size="4" orientation="horizontal" />
          <Flex direction="column" gap="2">
            {[1, 2, 3].map((i) => (
              <Box
                key={i}
                className="h-6 w-full bg-gray-200 rounded-md animate-pulse"
              />
            ))}
          </Flex>
          <Separator size="4" orientation="horizontal" />
          <Flex direction="row" align="center" gap="4">
            <Box className="h-20 w-1/2 bg-gray-200 rounded-md animate-pulse" />
            <Separator size="4" orientation="vertical" />
            <Box className="h-20 w-1/2 bg-gray-200 rounded-md animate-pulse" />
          </Flex>
          <Box className="h-10 w-full bg-gray-200 rounded-md animate-pulse" />
        </Card>
      </Grid>
    </Container>
  );
};

export const DumpsterDetails = ({ skuId }: { skuId: string }) => {
  const { website } = useTenantContext();
  const organizationId = website?.organization.id || '';
  const { isLoading, addToCart, shopConfiguration } = useShoppingCart();

  const skuQuery = trpcPricingListSkusOpen.useQuery({
    tenantId: organizationId,
    exclusiveId: skuId,
  });

  if (skuQuery.isLoading) {
    return <DumpsterDetailsSkeleton />;
  }

  const sku = skuQuery.data?.[0];

  if (!sku || !shopConfiguration) {
    return <div>Dumpster not found</div>;
  }

  const isBestDeal = website?.organization?.id === BEST_DEAL_ORG_ID;
  const bestDealInfo = isBestDeal ? getBestDealDumpsterInfo(sku.product?.cubicYardage) : null;

  const hasSubscription = !!sku.subscriptionSettings;
  const showSubscriptionRate =
    (sku.product?.containerType === 'CART' &&
      website?.showResidentialSubscriptionRates) ||
    (sku.product?.containerType === 'FRONT_LOAD' &&
      website?.showCommercialSubscriptionRates) ||
    (sku.product?.containerType === 'LIQUID_WASTE_CONTAINER' &&
      website?.showPortableToiletSubscriptionRates);

  const containerPhoto = onlineOrderingProductImage(sku.products[0]);

  const handleRentClick = async () => {
    await addToCart({
      skuId,
      productId: sku.product?.id ?? '',
    });
  };

  // Default features for regular dumpsters
  const defaultFeatures = bestDealInfo
    ? bestDealInfo.features.map((text) => ({ text }))
    : [
        {
          text: 'Convenient waste disposal solution for your project needs',
        },
        {
          text: 'Quick delivery and pickup scheduled around your timeline',
        },
      ];

  // Features for subscription services
  const subscriptionFeatures = [
    {
      text: `Billing cycle starts on ${formatBillingCycleAnchor(
        sku.subscriptionSettings?.billingCycleAnchor,
      )}`,
    },
    {
      text: `${
        sku.subscriptionSettings?.billingPreference === 'FORWARD'
          ? 'Billed in advance'
          : 'Billed in arrears'
      } ${sku.subscriptionSettings?.proration ? 'with proration' : ''}`,
    },
  ];

  // Add delivery & pickup fee information if available
  if (
    hasSubscription &&
    showSubscriptionRate &&
    sku.deliveryPrice?.chargeFee &&
    sku.deliveryPrice.chargeFee > 0
  ) {
    subscriptionFeatures.push({
      text: `Delivery: ${currency(sku.deliveryPrice.chargeFee || 0, {
        fromCents: true,
      }).format()}`,
    });
  }

  if (
    hasSubscription &&
    showSubscriptionRate &&
    sku.pickupPrice?.chargeFee &&
    sku.pickupPrice.chargeFee > 0
  ) {
    subscriptionFeatures.push({
      text: `Pickup: ${currency(sku.pickupPrice.chargeFee || 0, {
        fromCents: true,
      }).format()}`,
    });
  }

  // Add subscription info if neither delivery nor pickup has a charge
  if (
    !(
      (sku.deliveryPrice?.chargeFee && sku.deliveryPrice.chargeFee > 0) ||
      (sku.pickupPrice?.chargeFee && sku.pickupPrice.chargeFee > 0)
    ) &&
    sku.subscriptionItem?.priceName
  ) {
    subscriptionFeatures.push({
      text: `${sku.subscriptionItem.priceName}: ${currency(
        sku.subscriptionItem.chargeFee || 0,
        { fromCents: true },
      ).format()}`,
    });
  }

  // Choose which features to display
  const features = hasSubscription ? subscriptionFeatures : defaultFeatures;

  return (
    <>
      <Container size="4" p="4">
        <Breadcrumbs
          title={
            hasSubscription
              ? sku.subscriptionSettings?.name || 'Subscription Service'
              : `${parseCubicYardage(sku.product?.cubicYardage) || sku.product?.cubicYardage} YD Dumpster`
          }
          withinRentals
          light
        />

        <Grid columns={{ initial: '1', md: '2' }}>
          {containerPhoto && (
            <Box className="relative p-8 rounded-xl flex items-center justify-center">
              <StorageImage
                alt="container image"
                bucket="container-product-images"
                fit="contain"
                logoPath={containerPhoto}
              />
            </Box>
          )}

          <Card direction="column" gap="4" padding="6">
            <Heading as="h2" size="8" weight="bold">
              {hasSubscription
                ? sku.subscriptionSettings?.name || 'Subscription Service'
                : `${parseCubicYardage(sku.product?.cubicYardage) || sku.product?.cubicYardage} YD Dumpster`}
            </Heading>

            <Text size="2">
              {hasSubscription
                ? sku.subscriptionSettings?.description ||
                  sku.onlineOrderCopy ||
                  'Recurring service'
                : bestDealInfo
                ? `${bestDealInfo.dimensions} · ${bestDealInfo.weight}`
                : sku.onlineOrderCopy || ''}
            </Text>

            <Flex direction="row" gap="4">
              {hasSubscription ? (
                <>
                  <Flex align="center" gap="3">
                    <Box className="p-1 rounded-md bg-blue-950/5">
                      <IconRefresh size={16} />
                    </Box>
                    <Text size="1">
                      Billed every{' '}
                      {getFrequencyLabel(
                        sku.subscriptionSettings?.frequency,
                        sku.subscriptionSettings?.interval,
                      )}
                    </Text>
                  </Flex>

                  {hasSubscription &&
                    showSubscriptionRate &&
                    sku.deliveryPrice &&
                    sku.deliveryPrice.chargeFee > 0 && (
                      <Flex align="center" gap="3">
                        <Box className="p-1 rounded-md bg-blue-950/5">
                          <IconTruckDelivery size={16} />
                        </Box>
                        <Text size="1">
                          Delivery:{' '}
                          {currency(sku.deliveryPrice.chargeFee || 0, {
                            fromCents: true,
                          }).format()}
                        </Text>
                      </Flex>
                    )}

                  {hasSubscription &&
                    showSubscriptionRate &&
                    sku.pickupPrice &&
                    sku.pickupPrice.chargeFee > 0 && (
                      <Flex align="center" gap="3">
                        <Box className="p-1 rounded-md bg-blue-950/5">
                          <IconArrowsExchange size={16} />
                        </Box>
                        <Text size="1">
                          Pickup:{' '}
                          {currency(sku.pickupPrice.chargeFee || 0, {
                            fromCents: true,
                          }).format()}
                        </Text>
                      </Flex>
                    )}
                </>
              ) : (
                <>
                  {sku.daysLimit && sku.daysLimit > 0 ? (
                    <Flex align="center" gap="3">
                      <Box className="p-1 rounded-md bg-blue-950/5">
                        <IconCalendar size={16} />
                      </Box>
                      <Text size="1">
                        {sku.daysLimit} day
                        {sku.daysLimit && sku.daysLimit > 1 ? 's' : ''} rental
                      </Text>
                    </Flex>
                  ) : null}

                  {sku.materials.length > 0 && (
                    <Flex align="center" gap="3">
                      <Box className="p-1 rounded-md bg-blue-950/5">
                        <IconPackages size={16} />
                      </Box>
                      <Text size="1">
                        {sku.materials.map((m) => m.name).join(', ')}
                      </Text>
                    </Flex>
                  )}
                </>
              )}
            </Flex>

            <Separator size="4" orientation="horizontal" />

            <Flex direction="column" gap="2">
              {features.map((feature, index) => (
                <Flex key={index} align="center" gap="2">
                  <IconCheckmark className="w-6 h-6 flex-shrink-0" />
                  <Text size="2" weight="medium">
                    {feature.text}
                  </Text>
                </Flex>
              ))}
            </Flex>

            <Separator size="4" orientation="horizontal" />

            {hasSubscription &&
            showSubscriptionRate &&
            sku.subscriptionItem?.chargeFee ? (
              <Flex direction="row" align="center" gap="4">
                <Flex direction="column" gap="2" className="flex-1">
                  <Text
                    size="1"
                    weight="medium"
                    className="text-gray-900 uppercase"
                  >
                    Subscription Rate
                  </Text>

                  <Flex direction="row" align="center" gap="2">
                    <Text size="6" weight="bold">
                      {currency(
                        (sku.subscriptionItem &&
                          sku.subscriptionItem.chargeFee) ||
                          0,
                        {
                          fromCents: true,
                        },
                      ).format()}
                    </Text>
                    <Text
                      size="1"
                      weight="bold"
                      className="text-gray-900 uppercase"
                    >
                      /
                      {getFrequencyLabel(
                        sku.subscriptionSettings?.frequency,
                        sku.subscriptionSettings?.interval,
                      )}
                    </Text>
                  </Flex>
                </Flex>

                <Separator size="4" orientation="vertical" />

                <Flex direction="column" gap="2" className="flex-1">
                  <Text
                    size="1"
                    weight="medium"
                    className="text-gray-900 uppercase text-nowrap"
                  >
                    Additional Fees
                  </Text>

                  {hasSubscription &&
                    showSubscriptionRate &&
                    sku.deliveryPrice &&
                    sku.deliveryPrice.chargeFee > 0 && (
                      <Flex direction="row" align="center" gap="2">
                        <Text size="3" weight="bold">
                          {currency(sku.deliveryPrice.chargeFee || 0, {
                            fromCents: true,
                          }).format()}
                        </Text>
                        <Text size="1" className="text-gray-700">
                          Delivery
                        </Text>
                      </Flex>
                    )}

                  {sku.pickupPrice && sku.pickupPrice.chargeFee > 0 && (
                    <Flex direction="row" align="center" gap="2">
                      <Text size="3" weight="bold">
                        {currency(sku.pickupPrice.chargeFee || 0, {
                          fromCents: true,
                        }).format()}
                      </Text>
                      <Text size="1" className="text-gray-700">
                        Pickup
                      </Text>
                    </Flex>
                  )}
                </Flex>
              </Flex>
            ) : sku.dumpsterPrice?.chargeFee ? (
              <Flex direction="row" align="center" gap="4">
                <Flex direction="column" gap="2" className="flex-1">
                  <Text
                    size="1"
                    weight="medium"
                    className="text-gray-900 uppercase"
                  >
                    Online Pricing
                  </Text>

                  <Flex direction="row" align="center" gap="2">
                    <Text size="6" weight="bold">
                      {currency(
                        (sku.dumpsterPrice && sku.dumpsterPrice.chargeFee) ?? 0,
                        {
                          fromCents: true,
                        },
                      ).format()}
                    </Text>
                    {sku.daysLimit && sku.daysLimit > 0 ? (
                      <Text
                        size="1"
                        weight="bold"
                        className="text-gray-900 uppercase"
                      >
                        {sku.daysLimit} day{sku.daysLimit > 1 ? 's' : ''} <br />{' '}
                        rental
                      </Text>
                    ) : null}
                  </Flex>
                </Flex>

                <Separator size="4" orientation="vertical" />

                <Flex direction="column" gap="2" className="flex-1">
                  <Text
                    size="1"
                    weight="medium"
                    className="text-gray-900 uppercase text-nowrap"
                  >
                    Additional days for only
                  </Text>
                  <Flex direction="row" align="center" gap="2">
                    <Text size="6" weight="bold">
                      {currency(sku.additionalDaysPrice?.chargeFee ?? 0, {
                        fromCents: true,
                      }).format()}{' '}
                    </Text>
                    <Text
                      size="1"
                      weight="bold"
                      className="text-gray-900 uppercase"
                    >
                      +Per day
                    </Text>
                  </Flex>
                </Flex>
              </Flex>
            ) : null}

            {sku.product?.containerType === 'DUMPSTER' ? (
              <Button withArrow onClick={handleRentClick} loading={isLoading}>
                Rent dumpster
              </Button>
            ) : (
              <Button href="/contact-us" withArrow loading={isLoading}>
                Book now
              </Button>
            )}
          </Card>
        </Grid>
      </Container>
    </>
  );
};

export default DumpsterDetails;
